import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import DataTable from "examples/Tables/DataTable";
import MDTypography from "../MDTypography";
import {useAuth} from "context/AuthContext";

interface Event {
    sensorid: string;
    event_id: number;
    trigger_level: number;
    trigger_metric: string;
    file_path: string;
    event_description: string;
    time: string;
    location_name: string;
}

interface EventTableProps {
    location_id: number;
    timezone: string;
}





const EventTable: React.FC<EventTableProps> = ({ location_id, timezone }) => {
    const [data, setData] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState<string | null>(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

    const getPresignedUrl = async (objectKey: string) => {
        try {
            const decodedKey = decodeURIComponent(objectKey);
            const cleanKey = decodedKey.includes('https://')
                ? decodedKey.split('.com/')[1]
                : decodedKey;



            const s3Client = new S3Client({
                region: process.env.REACT_APP_AWS_REGION,
                credentials: {
                    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID!,
                    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY!,
                },
            });

            const command = new GetObjectCommand({
                Bucket: process.env.REACT_APP_S3_BUCKET_NAME!,
                Key: cleanKey,
            });

            return await getSignedUrl(s3Client, command, { expiresIn: 3600 });
        } catch (error) {
            console.error("Error generating presigned URL:", error);
            throw error;
        }
    };
    useEffect(() => {
        // Initialize audio player
        const player = document.createElement('audio');
        player.id = 'audio-player';
        player.controls = true;
        player.style.position = 'fixed';
        player.style.bottom = '20px';
        player.style.left = '20px';
        player.style.zIndex = '1000';
        player.style.backgroundColor = 'white';
        player.style.padding = '10px';
        player.style.borderRadius = '5px';
        player.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        player.style.display = 'none';
        player.style.width = '300px';
        player.style.margin = '10px';

        // Add event listeners
        player.addEventListener('ended', () => {
            setIsPlaying(null);
            player.style.display = 'none';
        });

        player.addEventListener('pause', () => {
            if (!player.ended) {
                setIsPlaying(null);
                // Don't hide the player on pause
            }
        });

        player.addEventListener('play', () => {
            player.style.display = 'block';
        });

        setAudioPlayer(player);
        document.body.appendChild(player);

        // Cleanup on unmount
        return () => {
            player.remove();
            setAudioPlayer(null);
        };
    }, []);

    const { sessionToken } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetch(
                    `${API_BASE_URL}/db/events?location_id=${encodeURIComponent(location_id)}&timezone=${encodeURIComponent(timezone)}`,
                    {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                setData(result);
                setError(null);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load events');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location_id, timezone]);
    const handlePlay = async (filePath: string) => {
        try {
            if (!audioPlayer) {
                console.error('Audio player not initialized');
                return;
            }

            // If this file is already playing, pause it
            if (isPlaying === filePath) {
                audioPlayer.pause();
                setIsPlaying(null);
                audioPlayer.style.display = 'none';
                return;
            }

            // If a different file is currently playing, pause it
            if (isPlaying !== null) {
                audioPlayer.pause();
                setIsPlaying(null);
            }

            // Get the presigned URL
            const pathOnly = filePath.includes('https://')
                ? filePath.split('.com/')[1]
                : filePath;

            const presignedUrl = await getPresignedUrl(pathOnly);

            // Update audio source and play
            audioPlayer.src = presignedUrl;

            // Always show the player when starting a new file
            audioPlayer.style.display = 'block';

            try {
                await audioPlayer.load();
                const playPromise = audioPlayer.play();

                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            setIsPlaying(filePath);
                            // Ensure player remains visible after successful play
                            audioPlayer.style.display = 'block';
                        })
                        .catch(error => {
                            console.error('Playback failed:', error);
                            // Keep the player visible if autoplay fails
                            audioPlayer.style.display = 'block';
                        });
                }
            } catch (playError) {
                console.error('Play error:', playError);
                // Ensure player is visible if there's an error
                audioPlayer.style.display = 'block';
            }
        } catch (error) {
            console.error('Error in handlePlay:', error);
        }
    };

    const handleDownload = async (filePath: string, time: string) => {
        try {
            const pathOnly = filePath.includes('https://')
                ? filePath.split('.com/')[1]
                : filePath;

            const presignedUrl = await getPresignedUrl(pathOnly);

            const link = document.createElement('a');
            link.href = presignedUrl;
            const filename = `audio_${format(new Date(time), 'yyyyMMdd_HHmmss')}.mp3`;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const columns = [
        {
            Header: (
                <MDTypography variant="h6" color="black">
                    Date
                </MDTypography>
            ),
            accessor: "date",
            Cell: ({ row }: { row: any }) => format(new Date(row.original.time), 'MMM d, yyyy')
        },
        {
            Header: (
                <MDTypography variant="h6" color="black">
                    Time
                </MDTypography>
            ),
            accessor: "timeOfDay",  // changed from "time"
            Cell: ({ row }: { row: any }) => format(new Date(row.original.time), 'h:mm:ss a')
        },
        {
            Header: (
                <MDTypography variant="h6" color="black">
                    Threshold Level (dBA)
                </MDTypography>
            ),
            accessor: "trigger_level",
            Cell: ({ value }: { value: number }) => value.toFixed(1)
        },
        {
            Header: (
                <MDTypography variant="h6" color="black">
                    Audio File
                </MDTypography>
            ),
            accessor: "file_path",
            Cell: ({ row }: { row: { original: Event } }) => (
                <>
                    <IconButton
                        type="button"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Prevents any parent onClick from firing
                            handlePlay(row.original.file_path);
                        }}
                        color={isPlaying === row.original.file_path ? "secondary" : "primary"}
                        aria-label={isPlaying === row.original.file_path ? "pause" : "play"}
                    >
                        {isPlaying === row.original.file_path ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <IconButton
                        type="button"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDownload(row.original.file_path, row.original.time);
                        }}
                        color="primary"
                        aria-label="download"
                    >
                        <DownloadIcon />
                    </IconButton>
                </>
            )
        }
    ];

    if (loading) {
        return <div>Loading events...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <DataTable
            table={{
                columns,
                rows: data
            }}
            onRowClick={() => {}} //disables row click navigation
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={true}
            noEndBorder
            pagination={{ variant:"gradient", color:"secondary" }}
        />
    );



};

export default EventTable;
